import { Youtube, Instagram, Facebook, Feather } from "lucide-react";
import { ASSETS, NAV_LINKS, SERVICES, LEGAL_DOCS } from "@/data/content";
import { scrollToId } from "@/lib/scroll";

const SOCIALS = [
    { icon: Youtube, label: "YouTube", href: "https://www.youtube.com/@iambrothatruth" },
    { icon: Instagram, label: "Instagram", href: "https://www.instagram.com/brothatruthonline/" },
    { icon: Facebook, label: "Facebook", href: "https://www.facebook.com/profile.php?id=100089075115738" },
];

const Footer = ({ onOpenLegal }) => {
    const year = new Date().getFullYear();

    return (
        <footer id="footer" data-testid="site-footer" className="relative border-t border-[#D9A441]/25 bg-[#05070C]">
            <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
                <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
                    <div>
                        <img
                            data-testid="footer-logo"
                            src={ASSETS.monogramWhite}
                            alt="BrothaTruth BT monogram — white letters with gold feather"
                            className="h-14 w-auto"
                            loading="lazy"
                        />
                        <p className="mt-5 flex items-center gap-2 text-sm italic text-[#D9A441]/90">
                            <Feather className="h-4 w-4" />
                            The pen is mightier than the sword.
                        </p>
                        <p className="mt-4 max-w-xs text-sm leading-relaxed text-[#F8F5EE]/50">
                            Educate. Empower. Establish your legacy. Knowledge, tools, and services for a brighter future.
                        </p>
                        <div className="mt-6 flex gap-3">
                            {SOCIALS.map((social) => (
                                <a
                                    key={social.label}
                                    data-testid={`footer-social-${social.label.toLowerCase()}`}
                                    href={social.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`BrothaTruth on ${social.label}`}
                                    className="flex h-10 w-10 items-center justify-center rounded-full border border-[#D9A441]/30 text-[#F8F5EE]/60 transition-all duration-300 hover:border-[#D9A441] hover:bg-[#D9A441]/10 hover:text-[#F4C46B]"
                                >
                                    <social.icon className="h-4 w-4" />
                                </a>
                            ))}
                        </div>
                    </div>

                    <nav aria-label="Footer navigation">
                        <h3 className="font-display text-xs font-bold uppercase tracking-[0.25em] text-[#D9A441]">Navigate</h3>
                        <ul className="mt-5 space-y-3">
                            {NAV_LINKS.map((link) => (
                                <li key={link.id}>
                                    <button
                                        data-testid={`footer-link-${link.id}`}
                                        onClick={() => scrollToId(link.id)}
                                        className="text-sm text-[#F8F5EE]/55 transition-colors duration-300 hover:text-[#F4C46B]"
                                    >
                                        {link.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <nav aria-label="Services">
                        <h3 className="font-display text-xs font-bold uppercase tracking-[0.25em] text-[#D9A441]">Services</h3>
                        <ul className="mt-5 space-y-3">
                            {SERVICES.map((service) => (
                                <li key={service.key}>
                                    <a
                                        data-testid={`footer-service-${service.key}`}
                                        href={service.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-[#F8F5EE]/55 transition-colors duration-300 hover:text-[#F4C46B]"
                                    >
                                        {service.title}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div>
                        <h3 className="font-display text-xs font-bold uppercase tracking-[0.25em] text-[#D9A441]">Legal</h3>
                        <ul className="mt-5 space-y-3">
                            {Object.entries(LEGAL_DOCS).map(([key, doc]) => (
                                <li key={key}>
                                    <button
                                        data-testid={`footer-legal-${key}`}
                                        onClick={() => onOpenLegal(key)}
                                        className="text-sm text-[#F8F5EE]/55 transition-colors duration-300 hover:text-[#F4C46B]"
                                    >
                                        {doc.title}
                                    </button>
                                </li>
                            ))}
                        </ul>
                        <a
                            data-testid="footer-website-link"
                            href="https://iselflawammaster.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-6 inline-block text-sm font-semibold text-[#D9A441] transition-colors hover:text-[#F4C46B]"
                        >
                            iselflawammaster.com
                        </a>
                    </div>
                </div>

                <div className="gold-hairline mt-14 h-px w-full" aria-hidden="true" />

                <div className="mt-7 flex flex-col items-center justify-between gap-3 text-xs text-[#F8F5EE]/40 sm:flex-row">
                    <p data-testid="footer-copyright">© {year} BrothaTruth. All rights reserved.</p>
                    <p>For education and empowerment — not legal advice.</p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
